import { Injectable } from '@angular/core';
import { addRxPlugin } from 'rxdb/plugins/core';
import { createRxDatabase, RxDatabase } from 'rxdb';
import { getRxStorageDexie } from 'rxdb/plugins/storage-dexie';
import { RxDBCleanupPlugin } from 'rxdb/plugins/cleanup';
import { RxDBLeaderElectionPlugin } from 'rxdb/plugins/leader-election';
import { RxDBDevModePlugin } from 'rxdb/plugins/dev-mode';
import { wrappedValidateAjvStorage } from 'rxdb/plugins/validate-ajv';
import { environment } from '../../../environments/environment';
import { SCHEMA_BY_NAME } from './schemas/schema-by-name';
import { EppDatabaseCollections } from './epp-database-collections';
import { getCurrentCmcVersion, getEmployee } from '../store';
import { firstValueFrom } from 'rxjs';
import { Store } from '@ngrx/store';
import { RxDBMigrationSchemaPlugin } from 'rxdb/plugins/migration-schema';
import { RxDBQueryBuilderPlugin } from 'rxdb/plugins/query-builder';
import { filter, map } from 'rxjs/operators';



@Injectable({
    providedIn: 'root'
})
export class RxdbRepository {

    private initPromise: Promise<RxDatabase<EppDatabaseCollections>>;
    private db: RxDatabase<EppDatabaseCollections>;

    constructor(private store: Store) {
        if (!environment.production) {
            addRxPlugin(RxDBDevModePlugin);
        }
        addRxPlugin(RxDBCleanupPlugin);
        addRxPlugin(RxDBLeaderElectionPlugin);
        addRxPlugin(RxDBMigrationSchemaPlugin);
        addRxPlugin(RxDBQueryBuilderPlugin);
    }

    getRxStorage() {
        return wrappedValidateAjvStorage({
            storage: getRxStorageDexie()
        });
    }

    public initDb(): Promise<RxDatabase<EppDatabaseCollections>> {
        if (this.db && !this.db.closed) {
            return Promise.resolve(this.db);
        }
        // Les appels concurrents partagent la même initialisation ; un échec la réinitialise pour permettre un nouvel essai
        if (!this.initPromise) {
            const initPromise = this.createDb().catch(async (error) => {
                console.error('Error while initializing RxDB database', error);
                if (this.initPromise === initPromise) {
                    this.initPromise = undefined;
                    await this.closeDb(this.db);
                    this.db = undefined;
                }
                throw error;
            });
            this.initPromise = initPromise;
        }
        return this.initPromise;
    }

    private async createDb(): Promise<RxDatabase<EppDatabaseCollections>> {
        this.db = await createRxDatabase<EppDatabaseCollections>({
            name: 'epp_db',
            // multiInstance:true,
            storage: this.getRxStorage(),
            allowSlowCount: true,
            cleanupPolicy: {
                minimumDeletedTime: 0,
                minimumCollectionAge: 1000 * 60 * 5,
                runEach: 1000 * 60 * 30
            }
        });
        await this.createCollections();
       // await this.waitForMigrations(this.db);
        return this.db;
    }

    /**
     * Ferme la base de cet onglet (logout) pour libérer IndexedDB avant sa suppression.
     * Une initialisation en cours est attendue : sinon la base qu'elle ouvre resterait ouverte et bloquerait la suppression.
     */
    public async close(): Promise<void> {
        const pendingInit = this.initPromise;
        this.initPromise = undefined;
        const db = this.db ?? await pendingInit?.catch(() => undefined);
        this.db = undefined;
        await this.closeDb(db);
    }

    private async closeDb(db: RxDatabase<EppDatabaseCollections> | undefined): Promise<void> {
        if (db && !db.closed) {
            await db.close();
        }
    }

    async createCollections() {
        const employee = await firstValueFrom(this.store.select(getEmployee));
        if (!employee?.countryIsoCode) {
            throw new Error('Cannot create RxDB collections: no employee country in store');
        }
        console.log('Start create collections');
        const suffixes = Object.keys(SCHEMA_BY_NAME);
        for (const suffix of suffixes) {
            const config = SCHEMA_BY_NAME[suffix];
            let collectionName = suffix === 'cmcprotocols' ? `${employee.countryIsoCode.toLowerCase()}_${suffix}` : `${suffix}`;
            if (!this.db.collections[collectionName]) {
                // console.log('init collectionName : ', collectionName);
                if (config.pull || config.push || config.pullHttp) { // condition à améliorer
                    const migrationStrategies = this.buildDefaultMigrations(config.schema, config.migrationStrategies);
                    await this.db.addCollections({
                        [collectionName]: {
                            schema: config.schema,
                            migrationStrategies: migrationStrategies
                        }
                    });
                } else {
                    continue;
                }
            }
        }
    }

    buildDefaultMigrations(schema: any, specificMigrations?: any) {
        const version = schema.version ?? 0;
        const migrations: any = {};
        for (let i = 1; i <= version; i++) {
            migrations[i] = (oldDoc: any) => ({ ...oldDoc });
        }
        return { ...migrations, ...(specificMigrations ?? {}) };
    }

    async waitForMigrations(db: RxDatabase) {
        await firstValueFrom(
            db.migrationStates().pipe(
                map(states =>
                    states
                        .filter(state => state.collection.schema.version > 0)
                        .every(state => state['status'] === 'DONE')
                ),
                filter(done => done === true)
            )
        );
    }


    async deleteCollection(collectionName: string): Promise<void> {
        console.info('⏳ Start deleting[' + collectionName + ']');
        const collection = this.db.collections[collectionName];

        if (collection) {
            try {
                await collection.close();
                indexedDB.deleteDatabase('rxdb-dexie-epp_db--0--' + collectionName);
                delete this.db.collections[collectionName];
                console.log(`✅ RxDB Collection "${collectionName}" deleted`);
            } catch (err) {
                console.error(`❌ Erreur lors de la suppression de la collection "${collectionName}" :`, err);
            }
        } else {
            console.warn(`⚠️ La collection "${collectionName}" n'existe pas dans db.collections`);
            try {
                indexedDB.deleteDatabase('rxdb-dexie-epp_db--0--' + collectionName);
            } catch (err) {
                console.warn(`⚠️ La base "${collectionName}" n'existe pas dans indexedDB`);
            }
        }
    }

    async createCollection(collectionName: string) {
        try {
            const collection = this.db.collections[collectionName];
            if (!collection) {
                const suffixes = Object.keys(SCHEMA_BY_NAME);

                for (const suffix of suffixes) {
                    if (collectionName.indexOf(suffix) > -1) {
                        await this.db.addCollections({
                            [collectionName]: { schema: SCHEMA_BY_NAME[suffix].schema }
                        });
                    }
                }
            } else {
                console.warn('collection ' + collectionName + 'already exists');
            }
        } catch (error) {
            if (error.code === 'DB3') {
                // continue
            } else {
                console.error('Error while create collection [' + collectionName + ']', error);
                throw error;
            }
        }

    }


    async getCurrentCmcVersion(): Promise<string> {
        return await firstValueFrom(this.store.select(getCurrentCmcVersion));
    }


}
